// La ÚNICA tabla de elegibilidad de las acciones MDM: si la acción aplica a ESTE equipo y, si no,
// por qué. Puro y sin React — los componentes pintan el botón apagado con el motivo escrito.
import { LoanDeviceMdmStatusEnum } from '@fiado/type-kit/bin/loanCredit/enums/LoanDeviceMdmStatusEnum';
import { MdmProviderEnum } from '@fiado/type-kit/bin/mdm/enums/MdmProviderEnum';
import { MdmLockModeEnum } from '@fiado/type-kit/bin/retailCatalog/enums/MdmLockModeEnum';
import {
    isUnderMdmControl,
    MISSING_LOCK_MODE_NOTE,
    readLockPhase,
    type MdmLockPhase,
} from './logic';
import { type MdmAction, type MdmActionKey } from './mdmActions';
import {
    NO_PERMISSION_LOCK,
    NO_PERMISSION_PIN_UNLOCK,
    NO_PERMISSION_REFRESH,
    NO_PERMISSION_UNLOCK,
    type MdmAbilities,
} from './permissions';
import type { MdmFleetDevice } from './types';

export interface MdmActionEligibility {
    enabled: boolean;
    /** Por qué no aplica. Se escribe bajo el botón apagado. */
    reason?: string;
    /** Advertencia cuando SÍ aplica pero el operador debe saber algo antes de apretar. */
    warning?: string;
    /** El motivo es que el proveedor no ofrece la acción: la ficha la pinta apagada, no la esconde. */
    byProvider?: true;
}

/** El expediente admite una sola operación a la vez: mientras haya una, nada más sale. */
export const IN_FLIGHT_REASON = 'Hay una orden en curso; se reintenta sola.';

/** La consulta sigue disponible siempre: no cambia el equipo y es lo que refresca el dato. */
const QUERY_ACTION_KEYS: readonly MdmActionKey[] = ['refresh'];

const NOT_UNDER_CONTROL = 'El equipo no está bajo control.';
const ALREADY_LOCKED = 'El equipo ya está bloqueado.';
const LOCK_ALREADY_SCHEDULED = 'Ya hay un bloqueo programado.';
const NOT_LOCKED = 'El equipo no está bloqueado.';
const TRUSTONIC_NOTIFY_WHILE_LOCKED =
    'Trustonic no acepta avisos con el equipo bloqueado: los rechaza con DEVICE_STATE_INVALID.';

const DATACULTR_NOTIFY_WARNING =
    'Datacultr acepta el aviso y no lo muestra: sus códigos reales están pendientes. Probado el 17-09, el equipo no mostró nada.';

const TRUSTONIC_VALIDITY_NEEDS_LOCK =
    'En Trustonic la vigencia solo se puede mover con el equipo bloqueado.';

const TRUSTONIC_NO_SCHEDULE_ONDEMAND = 'Trustonic no programa cortes en equipos pospago.';

/** El PIN abre un equipo bloqueado: sirve donde el candado cayó o pudo haber caído. El desmentido no. */
const PIN_UNLOCK_PHASES: readonly MdmLockPhase[] = ['locked', 'unconfirmed', 'cutoffPassed'];

const PIN_NEEDS_LOCK = 'El PIN solo sirve con el equipo bloqueado.';

const DATACULTR_NO_PIN =
    'Datacultr no entrega el código: su API responde que no hay pass key disponible, probado 6 veces del 11-ago al 17-sep.';

/** En Trustonic el temporal es solo de pospago, que es el modo bajo demanda del expediente. */
const TRUSTONIC_TEMPORARY_LOCK_NEEDS_ONDEMAND =
    'Trustonic solo admite el bloqueo temporal en equipos pospago, y este se enroló como prepago.';

const TRUSTONIC_NO_TEMPORARY_LOCK =
    'Trustonic no tiene el bloqueo temporal construido: al proveedor le falta el contrato completo.';

const DATACULTR_NO_TEMPORARY_LOCK = 'Datacultr no tiene bloqueo temporal.';

const MOTOSAFE_NO_TEMPORARY_LOCK =
    'Motosafe no tiene bloqueo temporal: solo bloquea y desbloquea al momento.';

const MOTOSAFE_NOTIFY_WHILE_LOCKED =
    'Con Motosafe el aviso no sale con el equipo bloqueado: el cliente ya recibió los avisos previos al corte.';

const MOTOSAFE_NO_SCHEDULE =
    'Motosafe no programa cortes: la vigencia no se mueve en el proveedor.';

const MOTOSAFE_NO_PIN =
    'Motosafe no tiene PIN de desbloqueo: el equipo se desbloquea con la orden de desbloqueo cuando tiene conexión.';

const DATACULTR_RELEASE_WARNING =
    'En Datacultr la liberación nunca se confirma: queda pendiente para siempre.';

/** Sin `unavailableNote` en el catálogo, esto es lo que se dice de una acción sin endpoint. */
export const NO_BACKEND_CONTRACT_REASON =
    'Esta acción todavía no tiene endpoint publicado: desde aquí no se ejecuta nada.';

/** La acción que el proveedor no ofrece para este equipo. */
function providerLimit(reason: string): MdmActionEligibility {
    return { enabled: false, reason, byProvider: true };
}

/** Lo que decide cada acción por su cuenta; sin `reason` la acción aplica. */
type ActionRule = (device: MdmFleetDevice, now: Date | string) => MdmActionEligibility | undefined;

const ACTION_RULES: Partial<Record<MdmActionKey, ActionRule>> = {
    lock: (device, now) => {
        if (device.mdmStatus === LoanDeviceMdmStatusEnum.LOCKED) {
            // Mientras el corte no cae el equipo sigue prendido: decir «ya está bloqueado» miente.
            const scheduled = readLockPhase(device, now) === 'scheduled';
            return { enabled: false, reason: scheduled ? LOCK_ALREADY_SCHEDULED : ALREADY_LOCKED };
        }
        if (device.mdmStatus !== LoanDeviceMdmStatusEnum.ACTIVE)
            return { enabled: false, reason: NOT_UNDER_CONTROL };
        return undefined;
    },
    unlock: (device) =>
        device.mdmStatus === LoanDeviceMdmStatusEnum.LOCKED
            ? undefined
            : { enabled: false, reason: NOT_LOCKED },
    temporaryLock: (device) => {
        if (device.mdmProvider === MdmProviderEnum.MOTOSAFE)
            return providerLimit(MOTOSAFE_NO_TEMPORARY_LOCK);
        if (device.mdmProvider === MdmProviderEnum.DATACULTR)
            return providerLimit(DATACULTR_NO_TEMPORARY_LOCK);
        // La modalidad comercial se lee del modo del expediente, no se afirma de memoria.
        return device.mdmLockMode === MdmLockModeEnum.SCHEDULED
            ? providerLimit(TRUSTONIC_TEMPORARY_LOCK_NEEDS_ONDEMAND)
            : providerLimit(TRUSTONIC_NO_TEMPORARY_LOCK);
    },
    notify: (device, now) => {
        if (!isUnderMdmControl(device)) return { enabled: false, reason: NOT_UNDER_CONTROL };
        if (device.mdmProvider === MdmProviderEnum.DATACULTR)
            return { enabled: true, warning: DATACULTR_NOTIFY_WARNING };
        const locked = readLockPhase(device, now) === 'locked';
        if (device.mdmProvider === MdmProviderEnum.MOTOSAFE)
            return locked ? { enabled: false, reason: MOTOSAFE_NOTIFY_WHILE_LOCKED } : undefined;
        // Trustonic solo rechaza el aviso con el candado ya puesto; mientras el corte no cae, pasa.
        return locked ? { enabled: false, reason: TRUSTONIC_NOTIFY_WHILE_LOCKED } : undefined;
    },
    extendValidity: (device) => {
        if (device.mdmProvider === MdmProviderEnum.MOTOSAFE)
            return providerLimit(MOTOSAFE_NO_SCHEDULE);
        if (!isUnderMdmControl(device)) return { enabled: false, reason: NOT_UNDER_CONTROL };
        if (device.mdmProvider === MdmProviderEnum.DATACULTR) return undefined;
        if (device.mdmLockMode === MdmLockModeEnum.ONDEMAND)
            return providerLimit(TRUSTONIC_NO_SCHEDULE_ONDEMAND);
        if (device.mdmLockMode !== MdmLockModeEnum.SCHEDULED)
            return { enabled: false, reason: MISSING_LOCK_MODE_NOTE };
        return device.mdmStatus === LoanDeviceMdmStatusEnum.LOCKED
            ? undefined
            : { enabled: false, reason: TRUSTONIC_VALIDITY_NEEDS_LOCK };
    },
    pinUnlock: (device, now) => {
        if (device.mdmProvider === MdmProviderEnum.MOTOSAFE) return providerLimit(MOTOSAFE_NO_PIN);
        if (device.mdmProvider === MdmProviderEnum.DATACULTR)
            return providerLimit(DATACULTR_NO_PIN);
        if (!isUnderMdmControl(device)) return { enabled: false, reason: NOT_UNDER_CONTROL };
        const phase = readLockPhase(device, now);
        return phase && PIN_UNLOCK_PHASES.includes(phase)
            ? undefined
            : { enabled: false, reason: PIN_NEEDS_LOCK };
    },
    release: (device) => {
        if (!isUnderMdmControl(device)) return { enabled: false, reason: NOT_UNDER_CONTROL };
        return device.mdmProvider === MdmProviderEnum.DATACULTR
            ? { enabled: true, warning: DATACULTR_RELEASE_WARNING }
            : undefined;
    },
};

/** El permiso que APAGA la acción con el motivo escrito; los demás la esconden. */
function describeMissingPermission(key: MdmActionKey, abilities: MdmAbilities): string | undefined {
    if (key === 'lock' && !abilities.canLock) return NO_PERMISSION_LOCK;
    if (key === 'unlock' && !abilities.canUnlock) return NO_PERMISSION_UNLOCK;
    if (key === 'pinUnlock' && !abilities.canPinUnlock) return NO_PERMISSION_PIN_UNLOCK;
    if (key === 'refresh' && !abilities.canRefresh) return NO_PERMISSION_REFRESH;
    return undefined;
}

/**
 * Acciones cuya clave de permiso todavía no está sembrada en ningún rol. Un botón apagado para todo
 * el mundo es ruido, así que estas se esconden en vez de pintarse.
 */
export function isActionHiddenByPermission(key: MdmActionKey, abilities: MdmAbilities): boolean {
    if (key === 'notify') return !abilities.canNotify;
    if (key === 'extendValidity') return !abilities.canExtendValidity;
    if (key === 'release') return !abilities.canRelease;
    return false;
}

/**
 * Si la acción aplica a ESTE equipo y, si no, por qué. Lo PERMANENTE se dice primero —falta de
 * endpoint, regla de la acción, permiso—; la orden en vuelo apaga al final, y solo lo que de otro
 * modo aplicaría: si no, el operador vuelve en una hora a probar algo que nunca va a poder.
 */
export function resolveActionEligibility(
    action: MdmAction,
    device: MdmFleetDevice,
    abilities: MdmAbilities,
    now: Date | string,
): MdmActionEligibility {
    const rule = ACTION_RULES[action.key]?.(device, now);

    if (!action.hasBackendContract) {
        return {
            enabled: false,
            reason: rule?.reason ?? action.unavailableNote ?? NO_BACKEND_CONTRACT_REASON,
            ...(rule?.reason && rule.byProvider ? { byProvider: true } : {}),
        };
    }
    if (rule?.reason)
        return rule.byProvider
            ? providerLimit(rule.reason)
            : { enabled: false, reason: rule.reason };

    const missingPermission = describeMissingPermission(action.key, abilities);
    if (missingPermission) return { enabled: false, reason: missingPermission };

    if (device.pendingOperations > 0 && !QUERY_ACTION_KEYS.includes(action.key)) {
        return { enabled: false, reason: IN_FLIGHT_REASON };
    }

    return rule?.warning ? { enabled: true, warning: rule.warning } : { enabled: true };
}

/** `true` cuando la acción se puede ejecutar contra este equipo con estos permisos. */
export function isActionEnabled(
    action: MdmAction,
    device: MdmFleetDevice,
    abilities: MdmAbilities,
    now: Date | string,
): boolean {
    return resolveActionEligibility(action, device, abilities, now).enabled;
}
